// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback, useState } from 'react';
import { useAppDispatch } from '@advisor-app/store/store';
import { subscriptionsApi } from '@advisor-app/store/api/engine/subscriptions-api';
import { useSafeUpdate } from '@shared/ui-kit/hooks';
import { userSlice } from '@shared/data-access/slices/user';
import { useTranslation } from 'react-i18next';

export const usePayment = () => {
  const { t } = useTranslation();
  const runSafeUpdate = useSafeUpdate();
  const dispatch = useAppDispatch();

  const [activeVoucher] = subscriptionsApi.useLazyActivateVoucherQuery();

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const applyVoucher = useCallback(
    async (voucher: string) => {
      if (voucher === '') {
        runSafeUpdate(() =>
          setError(t('common.please-enter-license-key', { ns: 'common' })),
        );
        return;
      }
      /* istanbul ignore next */
      try {
        runSafeUpdate(() => {
          setError('');
          setMessage('');
          setIsLoading(true);
        });

        const data = await activeVoucher({ voucher_code: voucher }).unwrap();

        runSafeUpdate(() => {
          if (data?.status) {
            dispatch(userSlice.actions.setUserData({ ...data }));
            setMessage(t('common.licensing-key-validated', { ns: 'common' }));
          } else {
            setError(t('common.an-error-has-occurred', { ns: 'common' }));
          }
          setIsLoading(false);
        });
        return !!data?.status;
      } catch (error) {
        console.error('[applyVoucher]', error);

        runSafeUpdate(() => {
          setError(t('common.an-error-has-occurred', { ns: 'common' }));
          setIsLoading(false);
        });
        return false;
      }
    },
    [dispatch, runSafeUpdate, activeVoucher, t],
  );

  return { applyVoucher, error, isLoading, message };
};
